import { Component, inject, OnInit, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { AuditLogEntryDto, Client } from '@fdm/api-client';
import { ReportFormStore, ReportFormPage, FormValidationService } from '../reports/services';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { StripHtmlPipe } from '../shared/pipes/strip-html.pipe';
import { FormatFieldNamePipe } from '../shared/pipes/format-field-name.pipe';
import { ReportForm } from '../reports/components/report-form/report-form';
import { ActivatedRoute } from '@angular/router';
import { filter, switchMap, forkJoin, catchError, of, combineLatest, debounceTime } from 'rxjs';
import { formatFieldNames } from '../utils/text-formatter';
import { FDMStatusOptions, FFSTypeOptions, filterTable, ReportTypeOptions, utcToLocal } from '@fdm/utils';
import { GetPeopleDetailsService } from '../services/get-people-details.service';
import { ApprovalFlowStateOptions } from '../utils/enum-maps';
import { FilterResponse, TableColumnFilterComponent } from '@fdm/shared';
import moment from 'moment';

interface AuditHistoryRow {
  tabName: string;
  fieldName: string;
  oldValue: string;
  newValue: string;
  changedBy: string;
  changedDate: string | undefined | Date;
  event: string;
}

@Component({
  selector: 'app-audit-history',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    ReactiveFormsModule,
    StripHtmlPipe,
    FormatFieldNamePipe,

    // Components
    TableColumnFilterComponent
  ],
  templateUrl: './audit-history.component.html',
  styleUrls: ['./audit-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class AuditHistoryComponent extends ReportForm implements OnInit {
  @ViewChild(MatSort) sort!: MatSort;

  private apiClient = inject(Client);
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private _formValidationService = inject(FormValidationService);
  private _getPeopleDetailsService = inject(GetPeopleDetailsService);

  generalTabId = toSignal(this._store.generalTabId$);
  private currentStep = toSignal(this._store.page$);

  public form!: FormGroup;

  public get step() {
    return ReportFormPage.AUDIT_HISTORY;
  }

  displayedColumns: string[] = ['changedDate', 'tabName', 'event', 'fieldName', 'oldValue', 'newValue', 'changedBy'];
  dataSource = new MatTableDataSource<AuditHistoryRow>([]);

  fieldFilterControl = new FormControl<string>('');
  availableFields: Array<{ value: string; label: string }> = [];
  filteredData: AuditHistoryRow[] = [];
  private generalTabIdValue!: string;
  private userNamesMap = new Map<string, string>();

  override ngOnInit(): void {
    this.initializeForm();
    super.ngOnInit();
    this._initTriggers();

    this.fieldFilterControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.applyFilter(value || '');
      });
  }

  private initializeForm(): void {
    this.form = this.fb.group({});
    this._formValidationService.registerForm(this.step, this.form);
  }

  private _initTriggers(): void {
    combineLatest([
      this.route.paramMap,
      this._store.general$,
      this._store.equipment$,
      this._store.approvalFlow$,
      this._store.risk$,
      this._store.ript$,
      this._store.mitigationResolution$
    ])
      .pipe(
        debounceTime(500),
        switchMap(([params]) => {
          const id = params.get('id') ?? '';
          if (id) {
            this.generalTabIdValue = id;
            this.loadAuditHistory(id);
          }
          return [];
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this._store.page$
      .pipe(
        filter((page) => page === this.step),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this._store.setPageValidity({ page: this.step, isStepValid: true });
      });
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  loadAuditHistory(generalTabId: string): void {
    this.apiClient.byGeneralTabAll(generalTabId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: AuditLogEntryDto[]) => {
          this.filteredData = data.map(entry => this.mapToRow(entry));

          this.dataSource.data = this.filteredData;
          this.tempTableData = [...this.dataSource.data];

          const uniqueFields = new Set(data.map(entry => entry.changedField).filter(f => f));
          const sortedFields = Array.from(uniqueFields).sort() as string[];
          this.availableFields = formatFieldNames(sortedFields);

          // Get distinct changedBy objectIds and fetch names
          const distinctObjectIds = Array.from(new Set(data.map(entry => entry.changedBy).filter(id => id))) as string[];
          this.loadUserNames(distinctObjectIds);

          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading audit history:', err);
        }
      });
  }

  mapToRow(entry: AuditLogEntryDto): AuditHistoryRow {
    const newValue = entry.newValue
      ? (
        FDMStatusOptions.find(item => item.value == entry.newValue)?.label ||
        ReportTypeOptions.find(item => item.value == entry.newValue)?.label ||
        FFSTypeOptions.find(item => item.value == entry.newValue)?.label ||
        entry.newValue
      )
      : '';

    return {
      tabName: entry.entityName || '',
      fieldName: entry.changedField || '',
      oldValue: entry.oldValue || '',
      newValue: newValue || '',
      changedBy: entry.changedBy || '',
      changedDate: utcToLocal(entry.changedDate)?.toDate(),
      event: entry.event?.toString() || ''
    };
  }

  formatChangedDate(date: string | Date | undefined): string {
    if (!date) return '';

    return moment(date).add(5, 'hours').format('DD MMMM YYYY HH:mm');
  }

  applyFilter(value: string): void {
    if (!value) {
      this.dataSource.data = this.filteredData;
    } else {
      this.dataSource.data = this.filteredData.filter(row =>
        row.fieldName === value
      );
    }
    this.tempTableData = [...this.dataSource.data];
  }

  private loadUserNames(objectIds: string[]): void {
    if (objectIds.length === 0) return;

    const requests = objectIds.map(id =>
      this._getPeopleDetailsService.getPeopleDetailsByObjectId(id).pipe(
        catchError(err => {
          console.error(`Error fetching name for ${id}:`, err);
          return of({ name: '' }); // Return empty name on error
        })
      )
    );

    forkJoin(requests)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (results) => {
          results.forEach((data, index) => {
            // Store the name if it exists, otherwise store empty string
            const displayName = data.name || '';
            this.userNamesMap.set(objectIds[index], displayName);
          });

          this.filteredData = this.filteredData.map(row => ({
            ...row,
            changedBy: this.userNamesMap.get(row.changedBy) || ''
          }));

          this.dataSource.data = this.filteredData;
          this.tempTableData = [...this.dataSource.data];
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error fetching people details:', err);
        }
      });
  }

  getStatusLabel(step: string): string {
    const option = ApprovalFlowStateOptions.find(opt => opt.value === step);
    return option ? option.label : step;
  }

  getFormattedValue(fieldName: string, value: string): string {
    if (fieldName?.toLowerCase() === 'currentstate') {
      return this.getStatusLabel(value);
    }
    return value;
  }

  filters: Record<string, FilterResponse | null> = {};
  tempTableData: any[] = [];

  filterTabdleCol(res: FilterResponse | null, column: string) {
    this.filters[column] = res;
    this.dataSource.data = filterTable(this.tempTableData, column, res);
  }

  isActive(column: string) {    
    return this.filters[column] != null;
  }
}
